import { type FC, useState } from "react";
import useSWR from "swr";
import { Download } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import type {
  CashflowSummary,
  ConsolidatedCashflowResponse,
  Currency,
} from "../../types";
import { cfApi, cfFetch } from "../../api/apiClient";
import { DashboardKpiStrip } from "./DashboardKpiStrip";
import { DashboardIncomeExpensePanel } from "./DashboardIncomeExpensePanel";
import { DashboardLiquidityPanel } from "./DashboardLiquidityPanel";
import { DashboardExpenseBreakdown } from "./DashboardExpenseBreakdown";
import { DashboardDebtAndScenario } from "./DashboardDebtAndScenario";
import type { ScenarioResponse } from "./DatamintDashboard.types";
import {
  downloadDashboardCsv,
  rollingYearRange,
} from "./dashboardUtils";
import { TransactionDateRangePicker } from "../transactions/TransactionDateRangePicker";
import { useCurrentCurrency } from "../../hooks/useCurrentCurrency";
import { useConsolidated } from "../../hooks/useConsolidated";

async function cfJson<T>(path: string): Promise<T> {
  const res = await cfFetch(path);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || res.statusText || String(res.status));
  }
  return res.json() as Promise<T>;
}

const DatamintDashboard: FC = () => {
  const { scopes } = useAuth();
  const hasCashflow = scopes?.includes("CASHFLOW") ?? false;

  const [dates, setDates] = useState(() => rollingYearRange());
  const { dateFrom, dateTo } = dates;
  const { currency: displayCurrency } = useCurrentCurrency();
  const { effectiveConsolidated } = useConsolidated();
  const [includeFutureInvoices, setIncludeFutureInvoices] = useState(true);

  const consolidatedKey =
    hasCashflow && effectiveConsolidated
      ? cfApi(
          `/cashflow/consolidated?${new URLSearchParams({
            displayCurrency,
            dateFrom,
            dateTo,
          }).toString()}`,
        )
      : null;

  const sameRange = new URLSearchParams({ dateFrom, dateTo }).toString();
  const usdKey = hasCashflow
    ? cfApi(`/cashflow?currency=USD&${sameRange}`)
    : null;
  const ilsKey = hasCashflow
    ? cfApi(`/cashflow?currency=ILS&${sameRange}`)
    : null;
  const eurKey = hasCashflow
    ? cfApi(`/cashflow?currency=EUR&${sameRange}`)
    : null;
  const scenarioKey = hasCashflow
    ? cfApi(
        `/cashflow/scenario?${new URLSearchParams({
          displayCurrency,
          dateFrom,
          dateTo,
          forecastMonths: "6",
          includeFutureInvoices: String(includeFutureInvoices),
        }).toString()}`,
      )
    : null;

  const {
    data: consolidated,
    isLoading: consolidatedLoading,
    error: consolidatedError,
  } = useSWR<ConsolidatedCashflowResponse>(
    consolidatedKey,
    consolidatedKey ? (u) => cfJson<ConsolidatedCashflowResponse>(u) : null,
  );

  const { data: usdRaw, isLoading: usdLoading } = useSWR<CashflowSummary>(
    usdKey,
    usdKey ? (u) => cfJson<CashflowSummary>(u) : null,
  );

  const { data: ilsRaw, isLoading: ilsLoading } = useSWR<CashflowSummary>(
    ilsKey,
    ilsKey ? (u) => cfJson<CashflowSummary>(u) : null,
  );
  const { data: eurRaw, isLoading: eurLoading } = useSWR<CashflowSummary>(
    eurKey,
    eurKey ? (u) => cfJson<CashflowSummary>(u) : null,
  );

  const { data: scenarioData, isLoading: scenarioLoading } =
    useSWR<ScenarioResponse>(
      scenarioKey,
      scenarioKey ? (u) => cfJson<ScenarioResponse>(u) : null,
    );

  /** Full-history cashflow for Current Balance KPI only (not filtered by dashboard date range). */
  const consolidatedBalanceKey =
    hasCashflow && effectiveConsolidated
      ? cfApi(
          `/cashflow/consolidated?${new URLSearchParams({
            displayCurrency,
          }).toString()}`,
        )
      : null;
  const soloBalanceKey =
    hasCashflow && !effectiveConsolidated
      ? cfApi(`/cashflow?currency=${encodeURIComponent(displayCurrency)}`)
      : null;

  const { data: consolidatedFull, isLoading: consolidatedFullLoading } =
    useSWR<ConsolidatedCashflowResponse>(
      consolidatedBalanceKey,
      consolidatedBalanceKey
        ? (u) => cfJson<ConsolidatedCashflowResponse>(u)
        : null,
    );

  const { data: soloFull, isLoading: soloFullLoading } =
    useSWR<CashflowSummary>(
      soloBalanceKey,
      soloBalanceKey ? (u) => cfJson<CashflowSummary>(u) : null,
    );

  const balanceSummary: CashflowSummary | undefined = effectiveConsolidated
    ? consolidatedFull
      ? (() => {
          const { meta: _m, ...rest } = consolidatedFull;
          return rest;
        })()
      : undefined
    : soloFull;
  const balanceSummaryLoading = effectiveConsolidated
    ? consolidatedFullLoading
    : soloFullLoading;

  const summary: CashflowSummary | undefined = consolidated
    ? (() => {
        const { meta: _m, ...rest } = consolidated;
        return rest;
      })()
    : undefined;
  const selectedSummaryByCurrency: Partial<
    Record<Currency, CashflowSummary | undefined>
  > = {
    USD: usdRaw,
    ILS: ilsRaw,
    EUR: eurRaw,
  };
  const selectedSummaryLoadingByCurrency: Partial<Record<Currency, boolean>> = {
    USD: usdLoading,
    ILS: ilsLoading,
    EUR: eurLoading,
  };
  const selectedSummary = selectedSummaryByCurrency[displayCurrency];
  const selectedSummaryLoading =
    selectedSummaryLoadingByCurrency[displayCurrency] ?? false;
  const activeSummary = effectiveConsolidated ? summary : selectedSummary;
  const mainWidgetsLoading = effectiveConsolidated
    ? consolidatedLoading
    : selectedSummaryLoading;

  const liquidityLoading = usdLoading || ilsLoading || eurLoading;

  const handleExport = () => {
    if (activeSummary)
      downloadDashboardCsv(activeSummary, "datamint-dashboard");
  };

  return (
    <div className="flex flex-1 flex-col overflow-auto bg-slate-50/60">
      <div className="w-full min-w-0 flex-1 space-y-6 py-6">
        <header className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Cashflow Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Monitor your financial health and project future cashflows
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <TransactionDateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              commitDateRange={(from, to) => {
                if (from === undefined && to === undefined) {
                  setDates(rollingYearRange());
                  return;
                }
                if (typeof from === "string" && typeof to === "string") {
                  setDates({ dateFrom: from, dateTo: to });
                }
              }}
              rangeHint="Show dashboard cashflow between"
              ariaRangeKind="Dashboard date range"
            />
            <button
              type="button"
              onClick={handleExport}
              disabled={!activeSummary}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export
            </button>
          </div>
        </header>

        {effectiveConsolidated && consolidatedError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Could not load consolidated cashflow. Try again later.
          </div>
        ) : null}

        <DashboardKpiStrip
          summary={activeSummary}
          balanceSummary={balanceSummary}
          balanceSummaryLoading={balanceSummaryLoading}
          currency={displayCurrency}
          isLoading={mainWidgetsLoading}
        />

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <DashboardIncomeExpensePanel
            summary={activeSummary}
            currency={displayCurrency}
            isLoading={mainWidgetsLoading}
          />
          <DashboardLiquidityPanel
            usdSummary={usdRaw}
            ilsSummary={ilsRaw}
            eurSummary={eurRaw}
            displayCurrency={displayCurrency}
            isLoading={liquidityLoading}
          />
        </div>

        <DashboardExpenseBreakdown
          summary={activeSummary}
          currency={displayCurrency}
          isLoading={mainWidgetsLoading}
          useConsolidatedSummary={effectiveConsolidated}
        />

        <DashboardDebtAndScenario
          points={scenarioData?.points ?? []}
          todayMonth={scenarioData?.todayMonth}
          currency={displayCurrency}
          cashflowSummary={activeSummary}
          cashflowSummaryLoading={mainWidgetsLoading}
          includeFutureInvoices={includeFutureInvoices}
          onToggleFutureInvoices={setIncludeFutureInvoices}
          isLoading={
            (effectiveConsolidated ? consolidatedLoading : false) ||
            scenarioLoading
          }
        />
      </div>
    </div>
  );
};

export default DatamintDashboard;
